import { useAtomValue } from 'jotai'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { omanCountryListAtom, omanFormatRawAtom } from '../../../stores/oman.store'
import { omanConfig } from '../../../oman.config'
import { useCurrentLanguage } from '@/core/utils'
import { useRegionsByCountry } from '../../../hooks/useRegionsByCountry'
import type { TOmanCountryItem } from '../../../types/oman.types'

const getCountryName = (item: TOmanCountryItem, lang: string = omanConfig.defaultLanguage): string =>
    lang === 'ar' && item.name_native ? item.name_native : item.name_english

const str = (v: unknown): string => (typeof v === 'string' ? v : '')

const getRegionName = (stateValue: string, regions: Array<{ name: string; code: string; value: string; id: string }>) => {
    const region = regions.find(item => item.name === stateValue || item.value === stateValue || item.code === stateValue || item.id === stateValue)
    return region?.name ?? stateValue
}

/**
 * Read-only summary of the shipping address shown when "billing same as
 * shipping" is checked. Pulls fields straight from the raw format response
 * (`ushop.detail_page`) so refreshes always reflect what the server has on
 * file — mirrors the legacy `billingHelper.js` source of truth.
 */
const BillingSummary = () => {
    const countryList = useAtomValue(omanCountryListAtom)
    const formatDataRaw = useAtomValue(omanFormatRawAtom)
    const currentLanguage = useCurrentLanguage()

    const detailPage = ((formatDataRaw?.ushop as Record<string, unknown> | undefined)?.detail_page as Record<string, unknown> | undefined) ?? {}

    const name = str(detailPage.fullname)
    const address1 = str(detailPage.address)
    const address2 = str(detailPage.address2)
    const city = str(detailPage.city)
    const state = str(detailPage.state)
    const countryCode = str(detailPage.country)
    const { regions } = useRegionsByCountry(countryCode || 'OM')

    const addressLine = [address1, address2].filter(Boolean).join(', ')
    const stateName = state ? getRegionName(state, regions) : ''
    const cityStateLine = [city, stateName].filter(Boolean).join(', ')
    const countryItem = countryList.find(c => c.country_code_2 === countryCode)
    const countryName = countryItem ? getCountryName(countryItem, currentLanguage.toLowerCase()) : countryCode

    const lines = [name, addressLine, cityStateLine, countryName].filter(Boolean)
    if (lines.length === 0) return null

    const lineSx = { fontSize: 14, fontWeight: 400, color: '#003764' } as const

    return (
        <Stack spacing={0} sx={{ pl: 4 }} data-testid="billing-summary-stack">
            {name && (
                <Typography variant="body2" sx={lineSx} data-testid="billing-summary-typography-1">
                    {name}
                </Typography>
            )}
            {addressLine && (
                <Typography variant="body2" sx={lineSx} data-testid="billing-summary-typography-2">
                    {addressLine}
                </Typography>
            )}
            {cityStateLine && (
                <Typography variant="body2" sx={lineSx} data-testid="billing-summary-typography-3">
                    {cityStateLine}
                </Typography>
            )}
            {countryName && (
                <Typography variant="body2" sx={{ ...lineSx, textTransform: 'uppercase' }} data-testid="billing-summary-typography-4">
                    {countryName}
                </Typography>
            )}
        </Stack>
    )
}

export default BillingSummary
